import React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { useApplyDraft, useDraftSummary } from '@/hooks/use-onboarding-interview';
import { useToastStore } from '@/store/toastStore';
import { ONBOARDING_SLOT } from '@/types/onboarding-interview';

const PRIMARY = '#FD5001';

interface Props {
  /** O `apply()` passou: o onboarding segue para os dados de pagamento. */
  onApplied: () => void;
  /** "Ajustar" — volta para a conversa, nunca para um formulário. */
  onAdjust?: () => void;
  /** Sem agente não há para onde voltar: troca "Ajustar" por outro ramo. */
  onPickAnother?: () => void;
}

/**
 * O fecho da entrevista (B5 da story): o que vai ser criado, em linguagem de
 * negócio, com dois botões.
 *
 * **Nada aqui é formatado no app** — preço, duração e as linhas da agenda
 * chegam prontos do `preview`, que é a mesma fonte dos widgets e do assistente.
 * "Confirmar" dispara o `apply()`, a única escrita de domínio do fluxo inteiro.
 */
export function InterviewSummary({ onApplied, onAdjust, onPickAnother }: Props) {
  const { data, isLoading, isError, refetch } = useDraftSummary();
  const apply = useApplyDraft();
  const { addToast } = useToastStore();

  const handleConfirm = async () => {
    try {
      await apply.mutateAsync();
      onApplied();
    } catch {
      addToast('Não consegui salvar sua configuração. Tente novamente.', 'error');
    }
  };

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator color={PRIMARY} />
      </View>
    );
  }

  if (isError || !data) {
    return (
      <ErrorState
        title="Não consegui montar o resumo"
        message="Sua conversa está salva. Tente de novo em um instante."
        onRetry={() => void refetch()}
      />
    );
  }

  const missing = data.pendingSlots ?? [];

  return (
    <View className="flex-1">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerClassName="px-6 py-6 gap-5"
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-1">
          <ThemedText className="font-bold text-2xl text-foreground">
            {data.activityLabel
              ? `Pronto! Entendi que você tem ${withArticle(data.activityLabel)}.`
              : 'Pronto! É isso que vou criar.'}
          </ThemedText>
          <ThemedText className="text-sm leading-5 text-foreground/55">
            Confira antes de eu criar. Dá para mudar tudo depois.
          </ThemedText>
        </View>

        {data.services.length > 0 ? (
          <Section
            icon="pricetags-outline"
            title={`${data.services.length} ${data.services.length === 1 ? 'serviço' : 'serviços'}`}
          >
            {data.services.map((service) => (
              <View
                key={service.name}
                className="flex-row items-start gap-3 rounded-xl bg-neutral-50 px-3 py-2.5"
              >
                <View className="flex-1 gap-0.5">
                  <ThemedText className="font-medium text-sm text-foreground">
                    {service.name}
                  </ThemedText>
                  <ThemedText className="text-xs text-foreground/50">{service.duration}</ThemedText>
                  {service.recurrence ? (
                    <ThemedText className="text-xs text-foreground/50">
                      {service.recurrence}
                    </ThemedText>
                  ) : null}
                  {service.capacity ? (
                    <ThemedText className="text-xs text-foreground/50">
                      {service.capacity}
                    </ThemedText>
                  ) : null}
                </View>
                <ThemedText className="font-semibold text-sm text-foreground">
                  {service.price}
                </ThemedText>
              </View>
            ))}
          </Section>
        ) : null}

        {data.scheduleLines.length > 0 ? (
          <Section icon="calendar-outline" title="Sua agenda">
            {data.scheduleLines.map((line, index) => (
              <ThemedText key={index} className="text-sm text-foreground/70">
                {line}
              </ThemedText>
            ))}
          </Section>
        ) : null}

        {data.locationLine ? (
          <Section icon="location-outline" title="Atendimento">
            <ThemedText className="text-sm text-foreground/70">{data.locationLine}</ThemedText>
          </Section>
        ) : null}

        {data.teamLine ? (
          <Section icon="people-outline" title="Quem atende">
            <ThemedText className="text-sm text-foreground/70">{data.teamLine}</ThemedText>
          </Section>
        ) : null}

        {data.paymentLine ? (
          <Section icon="wallet-outline" title="Pagamentos">
            <ThemedText className="text-sm text-foreground/70">{data.paymentLine}</ThemedText>
          </Section>
        ) : null}

        {missing.length > 0 ? (
          <View className="flex-row items-start gap-3 rounded-2xl bg-primary/5 px-4 py-3.5">
            <Ionicons name="information-circle-outline" size={20} color={PRIMARY} />
            <ThemedText className="flex-1 text-xs leading-5 text-foreground/65">
              No que faltou eu uso o padrão de quem trabalha com o mesmo que você:{' '}
              {missing.map(slotLabel).join(', ')}.
            </ThemedText>
          </View>
        ) : null}
      </ScrollView>

      <View className="gap-2 px-6 pb-4 pt-2">
        <Button onPress={() => void handleConfirm()} disabled={apply.isPending}>
          {apply.isPending ? (
            <View className="flex-row items-center gap-2">
              <ActivityIndicator size="small" color="white" />
              <ThemedText className="font-semibold text-white">Criando...</ThemedText>
            </View>
          ) : (
            <ThemedText className="font-semibold text-white">Confirmar</ThemedText>
          )}
        </Button>
        {onAdjust ? (
          <Button variant="outline" onPress={onAdjust} disabled={apply.isPending}>
            <ThemedText className="font-semibold text-foreground">Ajustar</ThemedText>
          </Button>
        ) : null}
        {onPickAnother ? (
          <Button variant="ghost" onPress={onPickAnother} disabled={apply.isPending}>
            <ThemedText className="font-semibold text-foreground/60">
              Escolher outro ramo
            </ThemedText>
          </Button>
        ) : null}
      </View>
    </View>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <View className="gap-3 rounded-2xl border border-border/40 bg-card p-4">
      <View className="flex-row items-center gap-2">
        <View className="h-7 w-7 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name={icon} size={14} />
        </View>
        <ThemedText className="font-semibold text-sm text-foreground">{title}</ThemedText>
      </View>
      <View className="gap-2">{children}</View>
    </View>
  );
}

/** "barbearia" → "uma barbearia"; "personal trainer" → "um personal trainer". */
function withArticle(label: string): string {
  const lower = label.toLowerCase();
  return `${lower.endsWith('a') ? 'uma' : 'um'} ${lower}`;
}

const SLOT_LABELS: Record<ONBOARDING_SLOT, string> = {
  [ONBOARDING_SLOT.ACTIVITY]: 'o seu ramo',
  [ONBOARDING_SLOT.BUSINESS_NAME]: 'o nome do negócio',
  [ONBOARDING_SLOT.TEAM]: 'quem atende',
  [ONBOARDING_SLOT.SERVICES]: 'os serviços',
  [ONBOARDING_SLOT.PRICING]: 'os valores',
  [ONBOARDING_SLOT.DURATION]: 'o tempo de cada atendimento',
  [ONBOARDING_SLOT.GRID]: 'os dias e horários',
  [ONBOARDING_SLOT.LOCATION]: 'onde você atende',
  [ONBOARDING_SLOT.ADDRESS]: 'o seu endereço',
  [ONBOARDING_SLOT.PAYMENTS]: 'como você recebe',
  [ONBOARDING_SLOT.RECURRENCE]: 'o plano fixo',
  [ONBOARDING_SLOT.CAPACITY]: 'quantas pessoas por horário',
};

function slotLabel(slot: ONBOARDING_SLOT): string {
  return SLOT_LABELS[slot] ?? slot;
}
